import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Loader2, Copy, Check, Share2, AtSign, Users, Sparkles, Flame, Quote, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import FounderVideoIntro from "@/components/founders/FounderVideoIntro";
import { founderLookup, checkHandle, reserveHandle, shareUrlFor, setTestimonial } from "@/lib/api";

const HANDLE_RE = /^[a-z0-9](?:[a-z0-9._-]{2,22}[a-z0-9])$/;

export default function Founders() {
  const { t } = useTranslation();
  const [emailInput, setEmailInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [founder, setFounder] = useState(null);

  // Pre-fill email from ?email=
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const e = p.get("email");
      if (e) setEmailInput(e);
    } catch (_) {
      // ignore url parsing errors
    }
  }, []);

  const doLookup = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!emailInput.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const f = await founderLookup(emailInput.trim());
      if (!f.is_founder) {
        setError("This email is on the waitlist but hasn't been assigned a Founder seat.");
        setFounder(null);
      } else {
        setFounder(f);
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setError("We couldn't find that email. Join the waitlist first.");
      } else {
        setError("Something went wrong. Try again.");
      }
      setFounder(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="founders-page">
      <SEO
        slug="founders"
        title="Founder 100 · Meet the first hundred — Instantxfer CashApp"
        description="The Founder Circle: members-only. Claim your @handle, see your seat, and browse the leaderboard of the first hundred believers in Instantxfer CashApp."
      />
      <PageHero
        eyebrow="The Founder Circle"
        title="For the first"
        italic="one hundred believers."
        body="Members-only. Enter the email you used to join — we'll pull up your seat and let you claim your @handle before anyone else."
        testId="founders-hero"
      />

      <FounderVideoIntro />

      <section className="pb-24 md:pb-32 bg-canvas">
        <div className="container-x">
          {!founder ? (
            <div className="max-w-xl">
              <form onSubmit={doLookup} noValidate className="card-flat p-6 md:p-8" data-testid="founder-lookup-form">
                <label className="block">
                  <span className="label-eyebrow">Your email</span>
                  <input
                    data-testid="founder-lookup-email"
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="the.email@you.used"
                    className="mt-2 w-full rounded-xl border border-forest/15 bg-canvas focus:bg-white focus:border-forest px-4 py-3 outline-none transition-colors"
                  />
                </label>
                {error && (
                  <div className="mt-4 text-sm text-destructive" data-testid="founder-lookup-error">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-accent mt-5"
                  data-testid="founder-lookup-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>Open my seat <ArrowUpRight className="w-4 h-4" /></>
                  )}
                </button>
                <p className="mt-4 text-xs text-ink/55">
                  Not a founder yet? <a href="/waitlist" className="underline text-forest">Join the waitlist</a> — the first 100 people become founders automatically.
                </p>
              </form>
            </div>
          ) : (
            <FounderDashboard founder={founder} setFounder={setFounder} />
          )}
        </div>
      </section>
    </main>
  );
}

function FounderDashboard({ founder, setFounder }) {
  const [handle, setHandle] = useState((founder.reserved_handle || "").replace(/^@/, ""));
  const [status, setStatus] = useState("idle"); // idle|checking|available|taken|invalid|saving|saved
  const [copied, setCopied] = useState(false);

  const shareLink = shareUrlFor(founder.referral_code);

  const validate = (v) => HANDLE_RE.test(v.trim().toLowerCase().replace(/^@/, ""));

  // Debounced availability check
  useEffect(() => {
    const val = (handle || "").trim().toLowerCase().replace(/^@/, "");
    if (!val) {
      setStatus("idle");
      return;
    }
    if (!validate(val)) {
      setStatus("invalid");
      return;
    }
    if (val === (founder.reserved_handle || "")) {
      setStatus("saved");
      return;
    }
    setStatus("checking");
    const t = setTimeout(async () => {
      try {
        const r = await checkHandle(val);
        setStatus(r.available ? "available" : "taken");
      } catch {
        setStatus("idle");
      }
    }, 350);
    return () => clearTimeout(t);
  }, [handle, founder.reserved_handle]);

  const save = async () => {
    const val = (handle || "").trim().toLowerCase().replace(/^@/, "");
    if (!validate(val)) return;
    setStatus("saving");
    try {
      const updated = await reserveHandle(founder.email, val);
      setFounder(updated);
      setStatus("saved");
    } catch (err) {
      if (err?.response?.status === 409) setStatus("taken");
      else setStatus("idle");
    }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me on Instantxfer CashApp",
          text: "I took a Founder seat on Instantxfer CashApp — here's my invite to skip the queue.",
          url: shareLink,
        });
      } catch (_) {
        // native share dismissed
      }
    } else {
      copyLink();
    }
  };

  const seatStr = String(founder.seat || 0).padStart(3, "0");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8" data-testid="founder-dashboard">
      {/* Seat card */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
        className="lg:col-span-5 rounded-3xl bg-forest text-canvas p-8 md:p-10 relative overflow-hidden"
        data-testid="seat-card"
      >
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-lime/20 blur-3xl" />
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-lime">
          Founder 100 · Seat reserved
        </div>
        <div className="mt-4 font-serif italic text-3xl text-canvas/85">Seat No.</div>
        <div className="font-display font-medium text-[8rem] leading-[0.85] tracking-tightest text-lime -ml-1">
          {seatStr}
        </div>
        <div className="mt-2 text-canvas/60 text-xs tracking-[0.22em] uppercase flex flex-wrap items-center gap-2">
          <span>{founder.full_name ? `${founder.full_name} · ` : ""}of 100</span>
          {founder.has_streak && (
            <span
              data-testid="dashboard-streak-badge"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-lime text-ink text-[10px] tracking-[0.22em] font-bold normal-case"
            >
              <Flame className="w-3 h-3" strokeWidth={2} /> on a streak
            </span>
          )}
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 text-canvas">
          <Stat icon={Users} kpi={founder.referrals_count} label="Friends referred" testId="dashboard-referrals" />
          <Stat icon={Sparkles} kpi="0.5%" label="Lifetime rate locked" />
        </div>
      </motion.div>

      {/* Right column */}
      <div className="lg:col-span-7 grid grid-cols-1 gap-6 md:gap-8">
        {/* Handle reservation */}
        <div className="card-flat p-6 md:p-8" data-testid="handle-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="label-eyebrow">Pre-claim your handle</div>
              <h3 className="mt-2 font-display font-medium text-forest text-2xl md:text-3xl tracking-tight">
                One name, everywhere on Instantxfer CashApp.
              </h3>
            </div>
            <div className="hidden md:block">
              <AtSign className="w-6 h-6 text-forest" strokeWidth={1.6} />
            </div>
          </div>
          <p className="mt-3 text-ink/70 text-sm max-w-lg">
            Founders pick first. Your handle is a payment identity — printable, scannable, portable across bank, wallet and QR.
          </p>

          <div className="mt-6 flex flex-col md:flex-row gap-2 items-stretch">
            <div className="flex-1 flex items-center rounded-xl border border-forest/15 bg-canvas focus-within:bg-white focus-within:border-forest transition-colors">
              <span className="pl-4 pr-1 font-mono text-forest text-lg">@</span>
              <input
                data-testid="handle-input"
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                placeholder="yourname"
                className="flex-1 bg-transparent py-3 pr-3 outline-none text-lg font-mono text-forest"
                spellCheck={false}
                autoCapitalize="off"
              />
              <span className="pr-4 text-ink/40 font-mono text-sm">.instant</span>
            </div>
            <button
              type="button"
              onClick={save}
              disabled={!(status === "available" || status === "saved") && status !== "saved"}
              className={`justify-center ${
                status === "saved" ? "btn-primary" : "btn-accent"
              } ${status === "checking" || status === "saving" ? "opacity-70" : ""} md:w-auto`}
              data-testid="handle-save-btn"
            >
              {status === "saving" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : status === "saved" ? (
                <><Check className="w-4 h-4" /> Reserved</>
              ) : (
                <>Reserve <ArrowUpRight className="w-4 h-4" /></>
              )}
            </button>
          </div>

          <HandleStatus status={status} handle={handle} data-testid="handle-status" />
        </div>

        {/* Share block */}
        {/* Testimonial editor */}
        <TestimonialCard founder={founder} setFounder={setFounder} />

        {/* Share block */}
        <div className="card-flat p-6 md:p-8 !bg-forest text-canvas !border-forest" data-testid="share-card">
          <div className="label-eyebrow !text-lime">Bring a friend</div>
          <h3 className="mt-2 font-display font-medium text-canvas text-2xl md:text-3xl tracking-tight">
            Every founder gets a queue-skipper.
          </h3>
          <p className="mt-3 text-canvas/70 text-sm max-w-lg">
            Anyone who joins through your link jumps ahead. While seats remain, they become a founder too — with the same lifetime perks as you.
          </p>

          <div className="mt-6 flex flex-col md:flex-row gap-2">
            <div
              className="flex-1 rounded-xl bg-canvas text-forest px-4 py-3 font-mono text-sm truncate"
              data-testid="dashboard-share-link"
            >
              {shareLink}
            </div>
            <button
              onClick={copyLink}
              type="button"
              className="btn-accent md:w-auto justify-center"
              data-testid="dashboard-copy-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied" : "Copy"}
            </button>
            <button
              onClick={nativeShare}
              type="button"
              className="justify-center inline-flex items-center gap-2 px-4 py-3 rounded-full border border-canvas/25 text-canvas hover:bg-canvas/10 transition-colors"
              data-testid="dashboard-share-btn"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>

          <div className="mt-4 text-[11px] text-canvas/55 font-mono tracking-widest uppercase">
            Code · {founder.referral_code}
          </div>
        </div>

        {/* Founder Kit download */}
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/founders/kit/${founder.referral_code}.pdf`}
          download
          className="flex items-center justify-between gap-3 rounded-3xl border border-forest/15 bg-white hover:bg-forest/[0.03] transition-colors p-6 md:p-7"
          data-testid="founder-kit-download"
        >
          <div>
            <div className="label-eyebrow">Your Founder Kit</div>
            <h3 className="mt-2 font-display text-forest text-2xl md:text-3xl tracking-tight">
              A one-page welcome, just for you.
            </h3>
            <p className="mt-2 text-sm text-ink/60 max-w-lg leading-relaxed">
              Seat No. <span className="font-mono text-forest">{String(founder.seat).padStart(3, "0")}</span>,
              your handle, a scannable QR to your invite, and a thank-you note we mean.
              Print it. Pin it. Or forward the PDF.
            </p>
          </div>
          <div className="shrink-0 w-14 h-14 rounded-full bg-lime text-ink grid place-items-center shadow-[0_10px_30px_-8px_rgba(217,249,66,0.55)]">
            <Download className="w-5 h-5" strokeWidth={2} />
          </div>
        </a>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, kpi, label, testId }) {
  return (
    <div>
      <div className="w-10 h-10 rounded-full bg-lime text-ink grid place-items-center">
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
      <div className="mt-3 font-display text-3xl tracking-tightest text-canvas" data-testid={testId}>
        {kpi}
      </div>
      <div className="text-canvas/60 text-xs tracking-[0.22em] uppercase mt-1">{label}</div>
    </div>
  );
}

function HandleStatus({ status, handle }) {
  const map = {
    idle: null,
    invalid: {
      color: "text-destructive",
      text: "Use 4–24 characters: letters, numbers, . _ or -",
    },
    checking: { color: "text-ink/60", text: "Checking availability…" },
    available: {
      color: "text-forest",
      text: <>@{handle.toLowerCase()}.instant is available.</>,
    },
    taken: {
      color: "text-destructive",
      text: "Sorry — another founder already claimed that one.",
    },
    saving: { color: "text-ink/60", text: "Reserving…" },
    saved: {
      color: "text-forest",
      text: (
        <span className="inline-flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4" /> Reserved forever. See you at launch.
        </span>
      ),
    },
  };
  const m = map[status];
  if (!m) return null;
  return (
    <div className={`mt-4 text-sm ${m.color}`} data-testid="handle-status">
      {m.text}
    </div>
  );
}

function TestimonialCard({ founder, setFounder }) {
  const MAX = 140;
  const [text, setText] = useState(founder.testimonial || "");
  const [state, setState] = useState("idle"); // idle | saving | saved | error
  const dirty = (text || "").trim() !== (founder.testimonial || "").trim();
  const remaining = MAX - (text || "").length;

  const save = async () => {
    setState("saving");
    try {
      const updated = await setTestimonial(founder.email, text.trim());
      setFounder(updated);
      setText(updated.testimonial || "");
      setState("saved");
      setTimeout(() => setState("idle"), 2000);
    } catch (e) {
      setState("error");
    }
  };

  return (
    <div className="card-flat p-6 md:p-8" data-testid="testimonial-card">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow">Your public one-liner</div>
          <h3 className="mt-2 font-display font-medium text-forest text-2xl md:text-3xl tracking-tight">
            Say it in one sentence.
          </h3>
        </div>
        <Quote className="w-6 h-6 text-forest hidden md:block" strokeWidth={1.6} />
      </div>
      <p className="mt-3 text-ink/70 text-sm max-w-lg">
        140 characters that will live on your public profile at{" "}
        <span className="font-mono text-forest">
          /founders/{founder.reserved_handle || "yourhandle"}
        </span>
        . Say why you took a seat, or what you&apos;re sending.
      </p>

      <textarea
        data-testid="testimonial-input"
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, MAX))}
        rows={3}
        placeholder="Instantxfer CashApp moved a birthday gift to my mum before her kettle boiled."
        className="mt-5 w-full rounded-xl border border-forest/15 bg-canvas focus:bg-white focus:border-forest px-4 py-3 text-ink outline-none transition-colors resize-none font-serif italic text-lg leading-snug"
      />
      <div className="mt-2 flex items-center justify-between">
        <span
          className={`text-xs ${remaining < 20 ? "text-destructive" : "text-ink/50"}`}
          data-testid="testimonial-remaining"
        >
          {remaining} characters left
        </span>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || state === "saving"}
          className={`justify-center ${
            state === "saved" ? "btn-primary" : "btn-accent"
          } ${!dirty || state === "saving" ? "opacity-70" : ""}`}
          data-testid="testimonial-save-btn"
        >
          {state === "saving" ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : state === "saved" ? (
            <><Check className="w-4 h-4" /> Saved</>
          ) : (
            <>Publish <ArrowUpRight className="w-4 h-4" /></>
          )}
        </button>
      </div>
    </div>
  );
}

